import type { QiGroup } from '../manifest';
import { formatQiXlsxDate } from '../validate';
/** Only labelled schema fields enter evidence. Unknown keys refuse the export so
 * a new criterion cannot silently lose its evidence. Raw JSON is never rendered. */
const labels: Record<string, string> = {
  stage: 'Injury stage',
  acquiredOutsideThisQuarter: 'Acquired outside this quarter',
  date: 'Event date',
  kind: 'Restriction type',
  finishingWeight: 'Finishing weight',
  startingWeight: 'Starting weight',
  middleWeight: 'Middle weight',
  plannedWeightLoss: 'Planned weight loss',
  away: 'Away from home',
  staffSupervised: 'Staff supervised',
  awayUnsupervised: 'Away from the home without staff supervision',
  majorInjury: 'Major injury',
  medicineKey: 'Medicine reference',
  activeIngredient: 'Active ingredient',
  skinOrWoundCare: 'Skin or wound care',
  dietarySupplement: 'Dietary supplement',
  shortTerm: 'Short term',
  prn: 'As required',
  antipsychotic: 'Antipsychotic administered',
  psychosis: 'Diagnosed psychosis',
  total: 'ADL total',
  incontinent: 'Incontinence',
  categories: 'GLOBIAD category',
  emergencyDepartment: 'Emergency department',
  admission: 'Hospital admission',
  workedDates: 'Worked date',
  score: 'Assessment score',
  recommended: 'Recommended service',
  received: 'Received service',
};
const collections: Record<string, string> = {
  injuries: 'Injuries',
  events: 'Events',
  falls: 'Falls',
  medicines: 'Medicines',
  administrations: 'Administrations',
  categories: 'Categories',
  workedDates: 'Worked dates',
};
const roots: Record<QiGroup, string[]> = {
  PI: ['injuries'],
  PR: ['events'],
  UPWL: ['finishingWeight', 'startingWeight', 'middleWeight', 'plannedWeightLoss'],
  FMI: ['falls'],
  MM: ['medicines', 'administrations', 'psychosis'],
  ADL: ['total'],
  IAD: ['incontinent', 'categories'],
  HP: ['events'],
  WF: ['workedDates'],
  CEI: ['score'],
  QOL: ['score'],
  AH: ['recommended', 'received'],
  QIT: [],
};
export function qiCriterionFields(
  group: QiGroup,
  criterion: unknown,
  formatEnum: (value: string) => string,
): { label: string; value: string | number }[] {
  if (!criterion || typeof criterion !== 'object' || Array.isArray(criterion)) return [];
  if (Object.keys(criterion).some((key) => !roots[group].includes(key)))
    throw new Error('qi_export_criterion_label_missing');
  const output: { label: string; value: string | number }[] = [];
  const scalar = (key: string, value: unknown, prefix = '') => {
    if (value === undefined) return;
    if (!labels[key]) throw new Error('qi_export_criterion_label_missing');
    const label = prefix + labels[key];
    if (value === null) output.push({ label, value: 'Not recorded' });
    else if (typeof value === 'boolean') {
      output.push({ label, value: value ? 'Yes' : 'No' });
      if (key === 'awayUnsupervised' && value)
        output.push({ label: prefix + 'Excluded reason', value: labels.awayUnsupervised! });
    } else if (typeof value === 'number' && Number.isFinite(value)) output.push({ label, value });
    else if (typeof value === 'string')
      output.push({
        label,
        value: ['date', 'workedDates'].includes(key)
          ? formatQiXlsxDate(value)
          : key === 'medicineKey'
            ? value
            : formatEnum(value),
      });
  };
  for (const key of roots[group]) {
    const value = (criterion as Record<string, unknown>)[key];
    if (Array.isArray(value))
      value.forEach((entry, index) => {
        const prefix = `${collections[key] ?? formatEnum(key)} ${index + 1}: `;
        if (entry && typeof entry === 'object' && !Array.isArray(entry))
          for (const [child, v] of Object.entries(entry)) scalar(child, v, prefix);
        else scalar(key, entry, prefix);
      });
    else scalar(key, value);
  }
  return output;
}
